import * as Blockly from 'blockly';
import { createModal } from './modal.js';
import { updateCode } from '@/scripts/shareFeature.js';
import boardConfig from '@/scripts/vernatics/config_board.json';
import { currLang } from '@/scripts/translation.js';

const BOARD_VERSION_KEY = 'aptcoder.codely.boardVersion';
const DEFAULT_BOARD_VERSION = 'v1';

export function setSelectedBoardVersion(boardVersion) {
  localStorage.setItem(BOARD_VERSION_KEY, boardVersion);
}

// Board version chosen the last time the user went through the BLE
// provisioning flow (showBoardChoiceModal). Python code generators run
// independently of that flow and have no other way to know which board
// is selected, so they read it back via this helper — defaulting to v1
// when nothing has been chosen yet (or when queried outside that flow).
export function getSelectedBoardVersion() {
  return localStorage.getItem(BOARD_VERSION_KEY) || DEFAULT_BOARD_VERSION;
}

// Single source of truth for per-board pin numbers — see the "_comment"
// in config_board.json. Every getter below reads from board.components;
// none of them hardcode a pin number for a specific board version.
export function getComponents() {
  const board = boardConfig[getSelectedBoardVersion()] || boardConfig[DEFAULT_BOARD_VERSION];
  return board.components || {};
}

export function getLedGreenPin() {
  return getComponents().ledGreen ?? 5;
}

export function getLedRedPin() {
  return getComponents().ledRed ?? 27;
}

export function getOledSclPin() {
  return getComponents().oledScl ?? 22;
}

export function getOledSdaPin() {
  return getComponents().oledSda ?? 21;
}

export function getServoPin() {
  return getComponents().servo ?? 32;
}

export function getMotorPins() {
  const components = getComponents();
  return {
    l1: components.motorL1 ?? 19,
    l2: components.motorL2 ?? 18,
    r1: components.motorR1 ?? 23,
    r2: components.motorR2 ?? 2,
  };
}

// ESP32 GPIO 34-39 are input-only — no internal pull-up/pull-down hardware
// at all, so requesting Pin.PULL_UP on one of them is a real config error
// (ESP-IDF logs "GPIO number error (input-only pad has no internal PU)" and
// silently skips it). Callers building `Pin(x, Pin.IN, ...)` python code
// should check this before appending ", Pin.PULL_UP".
export function pinSupportsPullUp(pin) {
  return !(pin >= 34 && pin <= 39);
}

export function getIrPins() {
  const components = getComponents();
  const left = components.irLeft ?? 15;
  const right = components.irRight ?? 14;
  return {
    left,
    right,
    leftSupportsPullUp: pinSupportsPullUp(left),
    rightSupportsPullUp: pinSupportsPullUp(right),
  };
}

// Activity 6 (Drawing Guesser) reuses the IR sensor pin pair as two extra
// output LEDs (LED_CIRCLE/LED_TRIANGLE) on V1, where irLeft/irRight (15/14)
// are ordinary output-capable GPIOs. On V2 those same components are 36/39
// — ESP32 input-only pins with no output driver at all, so `Pin(36,
// Pin.OUT)` raises `ValueError: pin can only be input` immediately. V2 has
// no free pins for these two, so stand in with no-op LEDs instead — shape
// detection still runs, just without a dedicated CIRCLE/TRIANGLE LED
// (LED_SQUARE and LED_BUSY still light up normally).
export function getShapeIndicatorLedSetupCode() {
  if (getSelectedBoardVersion() === 'v2') {
    return [
      'class _NoLed:',
      '    def on(self): pass',
      '    def off(self): pass',
      'LED_CIRCLE   = _NoLed()',
      'LED_TRIANGLE = _NoLed()',
    ].join('\n');
  }
  const { left, right } = getIrPins();
  return [`LED_CIRCLE   = Pin(${left}, Pin.OUT)`, `LED_TRIANGLE = Pin(${right}, Pin.OUT)`].join(
    '\n'
  );
}

export function getIrSensorPins() {
  const components = getComponents();
  const left = components.irSensorLeft ?? 13;
  const right = components.irSensorRight ?? 34;
  return {
    left,
    right,
    leftSupportsPullUp: pinSupportsPullUp(left),
    rightSupportsPullUp: pinSupportsPullUp(right),
  };
}

export function getUltrasonicPins() {
  const components = getComponents();
  return {
    trig: components.ultrasonicTrig ?? 25,
    echo: components.ultrasonicEcho ?? 26,
  };
}

export function getSpeakerPin() {
  const components = getComponents();
  return components.speaker ?? 22;
}

// I2S MEMS microphone — Board V2 only (its WS/SCK/SD pins collide with
// Board V1's motor pins, and no mic is physically wired on V1). Pin values
// come only from board.components.mic{Ws,Sck,Sd} (v2 in config_board.json);
// there is no V1 fallback default on purpose — the mic Blockly blocks are
// removed from the V1 toolbox entirely (see v1.removeBlocks), so this
// should never be called while V1 is selected.
export function getMicPins() {
  const components = getComponents();
  return {
    ws: components.micWs,
    sck: components.micSck,
    sd: components.micSd,
  };
}

// 6x6 NeoPixel LED matrix (36 pixels), same physical GPIO on both boards.
export function getLedMatrixPin() {
  return getComponents().ledMatrix ?? 4;
}

// Emits the NeoPixel init (matches the hardware team's validated test
// firmware) into a `matrix` object every mic block that draws on it can
// reuse, rather than re-deriving the neopixel import/pixel-count per block.
export function getLedMatrixSetupCode(varName = 'matrix') {
  const pin = getLedMatrixPin();
  return (
    `import neopixel\n` +
    `class _DimMatrix:\n` +
    `    def __init__(self, np):\n` +
    `        self.np = np\n` +
    `    def __setitem__(self, i, c):\n` +
    `        self.np[i] = (c[0] >> 2, c[1] >> 2, c[2] >> 2)\n` +
    `    def write(self):\n` +
    `        self.np.write()\n` +
    `${varName} = _DimMatrix(neopixel.NeoPixel(Pin(${pin}), 36))\n`
  );
}

// Emits the I2S init (pin map + sample rate, matching the hardware team's
// validated test firmware) plus a read_mic_rms() helper that samples one
// buffer and returns its RMS loudness. Every mic block calls this once per
// program rather than re-deriving the I2S/RMS math itself.
export function getMicSetupCode() {
  const { ws, sck, sd } = getMicPins();
  // `I2S`, `Pin`, and `math` are pre-injected into the on-device exec scope
  // by run_code() (see ESP32/model_manager.py) — same as every other block
  // relies on `Pin`/`PWM`/`ADC` being pre-injected rather than importing
  // them. Re-importing them here doesn't reliably bind names inside that
  // restricted exec() scope, so don't.
  return (
    [
      `mic = I2S(0, sck=Pin(${sck}), ws=Pin(${ws}), sd=Pin(${sd}), mode=I2S.RX, bits=32, format=I2S.MONO, rate=16000, ibuf=4000)`,
      '_mic_buf = bytearray(2048)',
      '',
      'def read_mic_rms():',
      '    n = mic.readinto(_mic_buf)',
      '    if not n:',
      '        return 0',
      '    sum_sq = 0',
      '    for i in range(0, n, 4):',
      '        sample = int.from_bytes(_mic_buf[i:i + 4], "little")',
      '        if sample >= 0x80000000:',
      '            sample -= 0x100000000',
      '        sample = sample >> 14',
      '        sum_sq += sample * sample',
      '    return math.sqrt(sum_sq / (n // 4))',
    ].join('\n') + '\n'
  );
}

// DHT11/DHT22 temperature+humidity sensor and the relay module — Board V2
// only. Their pins are shared, plug-one-at-a-time slots on the board (DHT
// shares GPIO15 with oledScl, relay shares GPIO27 with servo/PIR), so no
// activity mixes DHT with OLED, and no V1 fallback default is given on
// purpose — every DHT/relay block is removed from the V1 toolbox entirely
// (see v1.removeBlocks), so these should never be called while V1 is
// selected.
export function getDhtPin() {
  return getComponents().dht;
}

export function getRelayPin() {
  return getComponents().relay;
}

// Emits the DHT sensor init. Uses a bare `import dht` (not `from dht import
// DHT11`) — the mic's I2S NameError taught us `from X import Y` doesn't
// reliably bind names inside model_manager.py's restricted
// `exec(patched, scope)` sandbox, while a bare `import X` is proven safe
// (existing OLED blocks already do `import ssd1306` the same way).
export function getDhtSetupCode(sensorType = 'DHT11', varName = 'sensor') {
  const pin = getDhtPin();
  const cls = sensorType === 'DHT22' ? 'DHT22' : 'DHT11';
  return `import dht\n${varName} = dht.${cls}(Pin(${pin}))\n`;
}

// Emits the relay init — a plain digital output, no library needed, so no
// exec-scope risk at all (same as buzzer/LED). Starts off so a deployed
// activity never leaves the relay energized before the program's own logic
// decides otherwise.
export function getRelaySetupCode(varName = 'relay') {
  const pin = getRelayPin();
  return `${varName} = Pin(${pin}, Pin.OUT)\n${varName}.off()\n`;
}

// Board V2's two dedicated physical switches (SW1/SW2) — GPIO34/35, both in
// ESP32's input-only 34-39 range (see pinSupportsPullUp() above), so any
// `Pin(pin, Pin.IN, ...)` built from these must NOT request Pin.PULL_UP.
// V1-absent on purpose — no dedicated switches exist there, and any block
// using these is removed from the V1 toolbox (see v1.removeBlocks).
export function getSwitchPins() {
  const components = getComponents();
  return {
    sw1: components.switch1,
    sw2: components.switch2,
  };
}
export function getPirPin() {
  return getComponents().pir;
}

export function getPirSetupCode(varName = 'pir') {
  const pin = getPirPin();
  return `${varName} = Pin(${pin}, Pin.IN)  # HIGH = motion (assumed — confirm on real hardware)\n`;
}

// Board V1 drives a passive buzzer directly as a digital Pin.OUT — set it
// high, it makes noise. Board V2 has no buzzer, but does have a small
// speaker on its own GPIO driven via PWM (freq + duty), which needs an
// actual tone frequency to produce sound. To keep every existing
// `buzzer.value(1)` / `buzzer.value(0)` call site working unchanged, the
// V2 branch wraps the PWM speaker in a class that answers to the same
// `.value()` interface, translating "on" into a tone at DEFAULT_TONE_HZ
// and "off" into duty(0).
//
// Some activities' V1 buzzers are wired active-LOW (their BUZZ_ON/BUZZ_OFF
// constants are 0/1, i.e. value(0) means "sound") rather than the
// active-HIGH convention (value(1) means "sound") the wrapper above
// defaults to. `activeLow` flips which value the V2 wrapper treats as
// "on" so `.value(BUZZ_ON)` still sounds the speaker on V2 regardless of
// which polarity the calling activity's constants use.
const DEFAULT_TONE_HZ = 2000;

export function getBuzzerSetupCode(varName = 'buzzer', initValue = 0, activeLow = false) {
  if (getSelectedBoardVersion() === 'v2') {
    const pin = getSpeakerPin();
    const isOnCheck = activeLow ? 'not v' : 'v';
    return [
      `_spk_${varName} = PWM(Pin(${pin}))`,
      `_spk_${varName}.duty(0)`,
      `class _Speaker${varName}:`,
      `    def value(self, v=None):`,
      `        if ${isOnCheck}:`,
      `            _spk_${varName}.freq(${DEFAULT_TONE_HZ}); _spk_${varName}.duty(512)`,
      `        else:`,
      `            _spk_${varName}.duty(0)`,
      `${varName} = _Speaker${varName}()`,
    ].join('\n');
  }
  return `${varName} = Pin(26, Pin.OUT); ${varName}.value(${initValue})`;
}

// Same idea as getBuzzerSetupCode(), for the ESP-category buzzer blocks
// (Robo Star > Buzzer: beep/pattern/ir/button) that call `.on()`/`.off()`
// on a plain Pin.OUT rather than `.value()`. `pin` is whatever the block's
// own (V1) toolbox field/value carries — on V2 it's ignored in favor of the
// real speaker pin, since the field's board-patched default (see
// blockFieldMap in config_board.json) may still be edited by a student to
// something else, and V2 only has the one physical speaker to drive.
export function getBuzzerToggleSetupCode(varName = 'BUZZER', pin = 26) {
  if (getSelectedBoardVersion() === 'v2') {
    const speakerPin = getSpeakerPin();
    return [
      `_spk_${varName} = PWM(Pin(${speakerPin}))`,
      `_spk_${varName}.duty(0)`,
      `class _Speaker${varName}:`,
      `    def on(self):`,
      `        _spk_${varName}.freq(${DEFAULT_TONE_HZ}); _spk_${varName}.duty(512)`,
      `    def off(self):`,
      `        _spk_${varName}.duty(0)`,
      `${varName} = _Speaker${varName}()`,
    ].join('\n');
  }
  return `${varName} = Pin(${pin}, Pin.OUT)`;
}

// The PWM-style buzzer objects (bz/bpwm) already speak PWM's freq()/duty()
// interface — including the ones imported into an external device-side
// helper module (lie_helpers.py, knock_helpers.py) that call those methods
// directly — so on V2 they can point straight at the real speaker pin with
// no wrapper needed at all, same as V1 just on a different GPIO.
export function getPwmBuzzerSetupCode(varName = 'bz', freq = 440) {
  const pin = getSelectedBoardVersion() === 'v2' ? getSpeakerPin() : 26;
  return `${varName} = PWM(Pin(${pin})); ${varName}.freq(${freq}); ${varName}.duty(0)`;
}

export function getUltrasonicSetupCode(varName = 'uart') {
  if (getSelectedBoardVersion() === 'v2') {
    const { trig, echo } = getUltrasonicPins();
    return `TRIG_PIN = Pin(${trig}, Pin.OUT)\nECHO_PIN = Pin(${echo}, Pin.IN)`;
  }
  return `${varName} = UART(2, baudrate=9600, tx=25, rx=33)`;
}

export function getDistanceFunctionCode(fnName = 'get_distance', uartVar = 'uart') {
  if (getSelectedBoardVersion() === 'v2') {
    return `def ${fnName}():
    TRIG_PIN.value(0)
    time.sleep_us(2)
    TRIG_PIN.value(1)
    time.sleep_us(10)
    TRIG_PIN.value(0)
    try:
        duration = time_pulse_us(ECHO_PIN, 1, 30000)
    except OSError:
        return None
    if duration < 0:
        return None
    return round((duration * 0.0343) / 2, 2)`;
  }
  return `def ${fnName}():
    global ${uartVar}
    ${uartVar}.write(b'\\x55')
    time.sleep(0.02)
    if ${uartVar}.any() >= 2:
        data = ${uartVar}.read(2)
        if data and len(data) == 2:
            return ((data[0] << 8) | data[1]) / 10
    return None`;
}
// boardChoiceModal.js

function setBlockFieldOrShadowValue(block, fieldName, pin) {
  if (pin === undefined) return;

  // Pattern B: Blockly.FieldNumber directly on the block
  const directField = block.getField(fieldName);
  if (directField) {
    block.setFieldValue(String(pin), fieldName);
    return;
  }

  // Pattern A: value input holding a math_number shadow/real block
  const input = block.getInput(fieldName);
  const targetBlock = input && input.connection && input.connection.targetBlock();
  if (targetBlock && targetBlock.type === 'math_number') {
    targetBlock.setFieldValue(String(pin), 'NUM');
  }
  // if the input holds something else (a variable, an expression block),
  // it's been customized beyond a plain pin literal — leave it alone.
}

// Re-pins every block CURRENTLY ON THE WORKSPACE (not the toolbox) to the
// given board's components, using the same board-independent blockFieldMap.
// Call this any time the workspace's block set changes for a board reason:
// switching boards live, or right after loading a saved activity's XML
// (which may have been saved under a different board version).
export function syncWorkspaceBlockPins(boardVersion, workspace) {
  if (!workspace) return;

  const board = boardConfig[boardVersion];
  if (!board) {
    throw new Error(`Unknown board version: ${boardVersion}`);
  }

  const blockFieldMap = boardConfig.blockFieldMap || {};
  const components = board.components || {};

  workspace.getAllBlocks(false).forEach((block) => {
    const fieldMap = blockFieldMap[block.type];
    if (!fieldMap) return;

    Object.entries(fieldMap).forEach(([fieldName, componentName]) => {
      setBlockFieldOrShadowValue(block, fieldName, components[componentName]);
    });

    block.render();
  });

  // block.render() above only queues the re-render — Blockly's render
  // management system batches renders and normally flushes them on the
  // next user gesture (e.g. a mousedown on the workspace), which is why
  // clicking the canvas was the only thing that made the new pins show up
  // on the blocks. Force that flush immediately so it's visible without
  // requiring the user to touch the canvas.
  Blockly.renderManagement.triggerQueuedRenders(workspace);
}
export function showBoardChoiceModal() {
  return new Promise((resolve, reject) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'board-choice-modal';

    const currentVersion = getSelectedBoardVersion();

    wrapper.innerHTML = `
      <div class="board-choice-options">
        <label class="board-choice-option">
          <input
            type="radio"
            name="board-version"
            value="v1"
            ${currentVersion === 'v1' ? 'checked' : ''}
          />
          <span>${currLang['UI.BoardV1']}</span>
        </label>

        <label class="board-choice-option">
          <input
            type="radio"
            name="board-version"
            value="v2"
            ${currentVersion === 'v2' ? 'checked' : ''}
          />
          <span>${currLang['UI.BoardV2']}</span>
        </label>
      </div>

      <div class="board-choice-actions">
        <button
          type="button"
          class="board-choice-cancel"
        >
          ${currLang['UI.Cancel']}
        </button>

        <button
          type="button"
          class="board-choice-continue"
        >
          ${currLang['UI.Continue']}
        </button>
      </div>
    `;

    const continueBtn = wrapper.querySelector('.board-choice-continue');
    const cancelBtn = wrapper.querySelector('.board-choice-cancel');

    let settled = false;

    const resolveOnce = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      reject(error);
    };

    const modal = createModal({
      title: currLang['UI.SelectBoard'],
      closable: true,
      size: 'sm',
      content: () => wrapper,

      onClose: () => {
        // X / Escape / backdrop click
        rejectOnce(new Error('cancelled'));
      },
    });

    continueBtn.addEventListener('click', () => {
      const selected = wrapper.querySelector('input[name="board-version"]:checked');

      if (!selected) return;

      setSelectedBoardVersion(selected.value);
      resolveOnce(selected.value);
      modal.close();
    });

    cancelBtn.addEventListener('click', () => {
      rejectOnce(new Error('cancelled'));
      modal.close();
    });

    modal.open();
  });
}
export function loadBoardConfig(boardVersion) {
  const config = boardConfig[boardVersion];

  if (!config) {
    throw new Error(`Unknown board version: ${boardVersion}`);
  }

  return config;
}
function refreshBlocklyToolbox(workspace) {
  const toolbox = document.getElementById('toolbox');

  if (!toolbox || !workspace) {
    return;
  }

  workspace.updateToolbox(toolbox);
}
function removeBlocksFromToolboxXml(blockTypes, workspace) {
  const toolbox = document.getElementById('toolbox');

  if (!toolbox) {
    console.warn('Toolbox element not found');
    return;
  }

  blockTypes.forEach((blockType) => {
    const blocks = toolbox.querySelectorAll(`block[type="${blockType}"]`);

    blocks.forEach((block) => {
      block.remove();
    });
  });

  refreshBlocklyToolbox(workspace);
}
function updateBlockPinValues(blockType, pinValues, workspace) {
  const toolbox = document.getElementById('toolbox');

  if (!toolbox) {
    console.warn('Toolbox element not found');
    return;
  }

  const blocks = toolbox.querySelectorAll(`block[type="${blockType}"]`);

  blocks.forEach((block) => {
    Object.entries(pinValues).forEach(([valueName, pin]) => {
      // Pattern A: pin is a shadow `math_number` block inside a value input,
      // e.g. <value name="l1_pin"><block type="math_number"><field name="NUM">33</field>...
      const value = block.querySelector(`value[name="${valueName}"]`);
      const numberBlock = value && value.querySelector('block[type="math_number"]');
      const numField = numberBlock && numberBlock.querySelector('field[name="NUM"]');

      if (numField) {
        numField.textContent = String(pin);
        return;
      }

      // Pattern B: pin is a Blockly.FieldNumber directly on the block,
      // e.g. <field name="left_pin">13</field> (no value/shadow wrapper).
      const directField = block.querySelector(`field[name="${valueName}"]`);

      if (directField) {
        directField.textContent = String(pin);
      }
    });
  });

  refreshBlocklyToolbox(workspace);
}
function addBlockToToolbox(categoryName, blockXml, workspace) {
  const toolbox = document.getElementById('toolbox');

  if (!toolbox) {
    console.warn('Toolbox element not found');
    return;
  }

  const categories = toolbox.querySelectorAll('category');

  let targetCategory = null;

  categories.forEach((category) => {
    if (category.getAttribute('name') === categoryName) {
      targetCategory = category;
    }
  });

  if (!targetCategory) {
    console.warn(`Category "${categoryName}" not found`);
    return;
  }

  const parser = new DOMParser();

  const doc = parser.parseFromString(`<root>${blockXml}</root>`, 'text/xml');

  const block = doc.querySelector('block');

  if (!block) {
    console.warn('Invalid block XML');
    return;
  }

  const importedBlock = document.importNode(block, true);

  targetCategory.appendChild(importedBlock);

  refreshBlocklyToolbox(workspace);
}
export async function applyBoardConfiguration(boardVersion, workspace) {
  const board = boardConfig[boardVersion];
  if (!board) {
    throw new Error(`Unknown board version: ${boardVersion}`);
  }

  if (board.removeBlocks) {
    removeBlocksFromToolboxXml(board.removeBlocks, workspace);
  }

  const blockFieldMap = boardConfig.blockFieldMap || {};
  const components = board.components || {};
  Object.entries(blockFieldMap).forEach(([blockType, fieldMap]) => {
    const pins = {};
    Object.entries(fieldMap).forEach(([fieldName, componentName]) => {
      pins[fieldName] = components[componentName];
    });
    updateBlockPinValues(blockType, pins, workspace); // toolbox flyout
  });

  // NEW — repin blocks already placed on the canvas
  syncWorkspaceBlockPins(boardVersion, workspace);

  if (board.addBlocks) {
    board.addBlocks.forEach((block) => {
      addBlockToToolbox(block.category, block.xml, workspace);
    });
  }

  console.log(`Applied configuration for ${boardVersion}`);

  // Re-render the visible generated-code panel. On vernatics_esp/factory
  // pages that panel is the CodeMirror instance driven by updateCode()
  // (shareFeature.js) via a Blockly workspace change listener — switching
  // boards doesn't fire a Blockly event on its own, so without this the
  // panel kept showing the previous board's pins until the user clicked
  // the workspace and triggered a real change event.
  updateCode();
}
